import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { storage } from '@/lib/storage';
import { useAuthStore } from '@store/authStore';
import Button from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import PasswordInput from '@components/forms/PasswordInput';
import { RiArrowRightLine, RiMailLine, RiGoogleLine, RiMicrosoftLine, RiAlertLine } from "@remixicon/react";
import * as yup from "yup";
import { toast } from 'sonner';
import { apiClient } from '@/lib/api';
import { encodeData } from '@/utils';

// Simple Label component since it's not imported
const Label = ({ children, className, htmlFor }) => (
  <label htmlFor={htmlFor} className={`block text-sm font-medium text-gray-700 ${className || ''}`}>
    {children}
  </label>
);

const signInSchema = yup.object({
  email: yup
    .string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

// SignInForm component for reuse
export function SignInForm() {
  const navigate = useNavigate();
  const { setUser, setLoading: setAuthLoading, isLoading, setAuthType } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // Load remembered username on component mount
  useEffect(() => {
    const savedEmail = storage.getRememberedUsername();
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // ✅ Validate single field on change
  const validateField = async (name, value) => {
    try {
      // Use Yup's reach to get the schema for that specific field
      await (yup.reach(signInSchema, name)).validate(value);

      // If validation passes, remove that field's error
      setFieldErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        // Set only that field's error
        setFieldErrors((prev) => ({ ...prev, [name]: err.message }));
      }
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setError(null);

    try {
       // Validate inputs using Yup
      await signInSchema.validate({ email, password }, { abortEarly: false });

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        // Update Zustand store with user data
        setUser(data.user);

        // Handle username persistence based on "Remember me" checkbox
        if (rememberMe) {
          storage.rememberUsername(email);
        } else {
          storage.forgetUsername();
        }

        // Populate the authstore with the user + profile FIRST, then decide what to do.
        // We only fall back to the `getUserByUserId` precheck when the profile fetch
        // returns nothing — that endpoint currently 500s for some accounts in UAT, so
        // we keep it off the happy path.
        try {
          setAuthLoading(true);

          await useAuthStore.getState().fetchUserProfile();
          const userProfile = useAuthStore.getState().userProfile;

          // Profile loaded — we have everything we need to route the user.
          if (userProfile) {
            const status = userProfile.status;

            if (status !== 'ACTIVE') {
              const messages = {
                ONBOARDING_COMPLETED: "Your account creation is under review and will be completed soon. This review process usually takes up to 2 working days, but we'll notify you as soon as it's finished.",
                INACTIVE: "Your account has been deactivated. Contact support.",
                SUSPENDED: "Your account has been suspended. Contact support.",
                ARCHIVED: "Your account has been archived. Contact support to reactivate.",
              };
              toast.warning(messages[status] || "Your account is not active. Contact support.");
              await supabase.auth.signOut();
              navigate('/');
              setTimeout(() => setAuthType('signin'), 100);
              return;
            }

            if (!userProfile.is_active) {
              navigate('/welcome');
              return;
            }
          } else {
            // Profile didn't load. Fall back to the user precheck to decide
            // whether they need onboarding or profile-setup.
            let existingUser = null;
            try {
              existingUser = await apiClient.getUserByUserId(data.user.id);
            } catch (lookupErr) {
              console.error('SignIn: user lookup also failed:', lookupErr);
              toast.error("We couldn't load your account right now. Please try again, or contact support if this persists.");
              await supabase.auth.signOut();
              setAuthLoading(false);
              return;
            }

            if (!existingUser?.org_id) {
              if (existingUser?.role === 'broker' || existingUser?.role === 'operator') {
                navigate(`/onboarding?d=${encodeURIComponent(encodeData({ userTypes: [existingUser.role] }))}`);
                return;
              }
              navigate('/pofile-setup');
              return;
            }
          }
        } catch (error) {
          console.error('Organization check while signin error:', error);
        }

        // Check MFA status and redirect accordingly. Look across both `totp`
        // and `all` since SDK versions differ in which list reports which
        // statuses. A verified TOTP factor in either list means the user can
        // step up via mfa-verify; otherwise we route to enrollment.
        const { data: factors } = await supabase.auth.mfa.listFactors();

        const totpFactors = [
          ...(factors?.totp || []),
          ...((factors?.all || []).filter(f => f.factor_type === 'totp')),
        ];
        const hasVerifiedTotp = totpFactors.some(f => f.status === 'verified');

        // skip mfa in dev environment, or for internal @gotrustjet.com accounts
        const isInternalEmail = (email || '').toLowerCase().endsWith('@gotrustjet.com');
        if (process.env.REACT_APP_ENVIRONMENT === "DEV" || isInternalEmail) {
          navigate("/dashboard");
        } else {
          if (hasVerifiedTotp) {
            navigate('/mfa-verify');
          } else {
            navigate('/mfa-enroll');
          }
        }
      }
    } catch (err) {
       if (err instanceof yup.ValidationError) {
        const errors = {};
        err.inner.forEach((e) => {
          if (e.path) errors[e.path] = e.message;
        });
        setFieldErrors(errors);
      } else {
        setError(err instanceof Error ? err.message : "Failed to sign in");
      }
      setAuthLoading(false);
    }
  };

  const handleCancel = () => {
    setAuthType('home');
  };

  const handleGoogleSignIn = async () => {
    try {
      setAuthLoading(true);
      setError(null);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth-callback`
        }
      });

      if (error) throw error;
    } catch (err) {
      setError(err.message);
      setAuthLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    try {
      setAuthLoading(true);
      setError(null);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          redirectTo: `${window.location.origin}/auth-callback`,
          scopes: 'email'
        }
      });

      if (error) throw error;
    } catch (err) {
      setError(err.message);
      setAuthLoading(false);
    }
  };

  return (
    <>

     <div className="text-center lg:text-left mb-6">
        <h2 className="text-3xl font-bold text-primary-500">Sign In</h2>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 mb-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <RiAlertLine className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Sign In Error
              </h3>
              <div className="mt-1 text-sm text-red-700">
                {error}
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSignIn} className="space-y-4">

       <div className="space-y-2">
          <Label className="!gap-1" htmlFor="email">Email <span className="text-primary-500">*</span></Label>
          <div className="relative">
            <span className="absolute left-3 top-[23px] transform -translate-y-1/2 text-[#5C5C5C]">
              <RiMailLine size={15} />
            </span>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                validateField("email", e.target.value);
              }}
              required
              disabled={isLoading}
              className={`h-11 !pl-8 !bg-white !border-2 ${fieldErrors.email ? 'border-red-500' : '!border-border-light'} !text-slate-500 !rounded-lg`}
            />
          </div>
          {fieldErrors.email && (
            <p className="text-red-500 text-sm">{fieldErrors.email}</p>
          )}

        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="!gap-1" htmlFor="password">Password <span className="text-[#0C3E71]">*</span></Label>
            <button
              type="button"
              className="text-xs text-[#5C5C5C]"
              onClick={() => navigate('/forgot-password')}>
              Forgot password?
            </button>
          </div>
          
          <PasswordInput
            id="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              validateField("password", e.target.value);
            }}
            placeholder="••••••••"
            required={true}
            disabled={isLoading}
            showLabel={false}
            hasError={!!fieldErrors.password}
          />

          {fieldErrors.password && (
            <p className="text-red-500 text-sm">{fieldErrors.password}</p>
          )}

        </div>

        <div className="flex items-center space-x-2 mt-2">
          <input
            type="checkbox"
            id="rememberMe"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="form-checkbox h-4 w-4 text-[#0C3E71] border-gray-300 rounded"
          />
          <label htmlFor="rememberMe" className="text-sm">
            Remember me
          </label>
        </div>
      </form>


      {/* OAuth Sign-in Options */}
      <div className="space-y-3 mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <Button
            type="button" 
            variant="secondary"
            mode="stroke"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-full inline-flex justify-center items-center px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 rounded-full"
          >
            <RiGoogleLine className="w-5 h-5 mr-2 text-red-500" />
            Continue with Google
          </Button>

          <Button
            type="button" 
            variant="secondary"
            mode="stroke"
            onClick={handleMicrosoftSignIn}
            disabled={isLoading}
            className="w-full inline-flex justify-center items-center px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 rounded-full"
          >
            <RiMicrosoftLine className="w-5 h-5 mr-2 text-blue-500" />
            Continue with Microsoft
          </Button>
        </div>
      </div>

      <div className="flex space-x-4 mt-auto pt-6">
          <Button
            type="button" 
            variant="secondary"
            mode="stroke"
            className="flex-1 !border-[#EBEBEB] !font-normal rounded-full"
            disabled={isLoading}
            onClick={handleCancel}
          > 
            Cancel
          </Button>
          <Button
            type={'submit'}
            className="flex-1 !font-normal bg-primary-500 hover:bg-primary-600 disabled:bg-[#F7F7F7] disabled:border disabled:border-[#EBEBEB] disabled:text-gray-400 rounded-full"
            onClick={handleSignIn}
            disabled={!email || !password || isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign In'} <RiArrowRightLine className="inline-block ml-1" />
          </Button>
      </div>
    </>
  );
}

// Default export for backward compatibility
export default function SignIn() {
  return <SignInForm />;
}
